import { Box, IconButton, Typography, Collapse, Button, Fab, Tooltip } from "@mui/material";
import { useState, useEffect, useRef, useMemo } from "react";
import { FlowDoHeader } from "../FlowDoHeader/FlowDoHeader";
import { KanbanPageSelector } from "../KanbanPageSelector/KanbanPageSelector";
import { KanbanCategorySelector } from "../KanbanCategorySelector/KanbanCategorySelector";
import { KanbanBoard } from "../KanbanBoard/KanbanBoard";
import { ListView } from "../ListView/ListView";
import { CreateTaskForm } from "../CreateTaskForm/CreateTaskForm";
import { BaseModalComponent } from "../../../common/BaseModalComponent";
import { useKanbanData } from "../../hooks/useKanbanData";
import { useCreateKanbanPage } from "../../hooks/useCreateKanbanPage";
import { useModals } from "../../../../context/ModalContext";
import { CreateCategoryForm } from "../CreateCategoryForm/CreateCategoryForm";
import InfoIcon from "@mui/icons-material/Info";

import AddIcon from "@mui/icons-material/Add";
import { colors } from "../../../ui/colors";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks/redux";
import { fetchBenefits } from "../../../../permissions/benefits/redux/benefits.thunks";
import { selectEntitlement } from "../../../../permissions/benefits/redux/benefits.selectors";
import { EBenefitTypes } from "../../../../permissions/benefits/enums/benefits.enums";

type ViewMode = "kanban" | "list";

interface FlowDoMainLayoutProps {
  isPreviewMode?: boolean;
}

export const FlowDoMainLayout = ({ isPreviewMode }: FlowDoMainLayoutProps) => {
  const [viewMode, setViewMode] = useState<ViewMode>("kanban");
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);

  // NEW: collapse state for description
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(false);
  const [taskCreationStatus, setTaskCreationStatus] = useState<string | null>(null);

  const createTaskTriggerRef = useRef<HTMLButtonElement>(null);
  const createCategoryTriggerRef = useRef<HTMLButtonElement>(null);
  const hasCreatedInitialPageRef = useRef<boolean>(false);
  const { closeModal } = useModals();

  const { pages, isLoading: isLoadingData } = useKanbanData();
  const createPageMutation = useCreateKanbanPage();
  const dispatch = useAppDispatch();

  // Entitlement for Flow-DO
  const flowDoEntitlement = useAppSelector(selectEntitlement(EBenefitTypes.FLOW_DO));
  const TASK_LIMIT = flowDoEntitlement?.limit ?? 0;
  const currentTaskCount = flowDoEntitlement?.used ?? 0;
  const isLimitReached = currentTaskCount >= TASK_LIMIT;

  useEffect(() => {
    dispatch(fetchBenefits());
  }, [dispatch]);

  useEffect(() => {
    if (!isPreviewMode && !isLoadingData && pages.length === 0 && !hasCreatedInitialPageRef.current && !createPageMutation.isPending) {
      hasCreatedInitialPageRef.current = true;
      createPageMutation.mutate("Business Tasks", {
        onSuccess: (newPage) => setSelectedPageId(newPage.uid),
        onError: () => {
          hasCreatedInitialPageRef.current = false;
        },
      });
      createPageMutation.mutate("Other Tasks", {
        onSuccess: (newPage) => setSelectedPageId(newPage.uid),
        onError: () => {
          hasCreatedInitialPageRef.current = false;
        },
      });
    }
    if (pages.length > 0 && hasCreatedInitialPageRef.current) {
      hasCreatedInitialPageRef.current = false;
    }
  }, [isLoadingData, pages.length, createPageMutation.isPending, isPreviewMode]);

  useEffect(() => {
    if (pages.length > 0 && !selectedPageId) {
      setSelectedPageId(pages[0].uid);
    }
  }, [pages, selectedPageId]);

  const prevPageIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (selectedPageId) {
      const page = pages.find((p) => p.uid === selectedPageId);
      const isPageChange = prevPageIdRef.current !== selectedPageId;
      prevPageIdRef.current = selectedPageId;

      if (page && page.categories.length > 0) {
        // Auto-select first category if switching pages OR if none selected
        if (!selectedCategoryId || isPageChange) {
          const sorted = [...page.categories].sort((a, b) => (a.order || 0) - (b.order || 0));
          setSelectedCategoryId(sorted[0].uid);
        }
      } else {
        setSelectedCategoryId(null);
      }
    } else {
      setSelectedCategoryId(null);
      prevPageIdRef.current = null;
    }
  }, [selectedPageId, pages, selectedCategoryId]);

  const selectedPage = useMemo(() => pages.find((p) => p.uid === selectedPageId), [pages, selectedPageId]);
  const categories = selectedPage?.categories || [];

  const tasks = useMemo(() => {
    if (!selectedCategoryId) return [];
    const category = categories.find((c) => c.uid === selectedCategoryId);
    return category?.tasks || [];
  }, [categories, selectedCategoryId]);

  const handleCreateTaskClick = (status?: string) => {
    // if (isLimitReached) return;
    setTaskCreationStatus(status?.toUpperCase() ?? null);
    if (selectedCategoryId) {
      createTaskTriggerRef.current?.click();
    } else if (selectedPageId && categories.length > 0) {
      const firstCategory = categories[0];
      setSelectedCategoryId(firstCategory.uid);
      setTimeout(() => createTaskTriggerRef.current?.click(), 100);
    }
  };

  const handleCreateCategoryClick = () => {
    if (selectedPageId) {
      createCategoryTriggerRef.current?.click();
    }
  };

  const handleTaskCreated = () => closeModal("FlowDo-CreateTask");
  const handleCategoryCreated = () => closeModal("FlowDo-CreateCategory");

  const handlePageDeleted = () => {
    if (pages.length > 1) {
      const remainingPages = pages.filter((p) => p.uid !== selectedPageId);
      setSelectedPageId(remainingPages.length > 0 ? remainingPages[0].uid : null);
    } else {
      setSelectedPageId(null);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        p: 3,
        overflowY: "auto",
        scrollbarGutter: "stable",
      }}
    >
      {/* Description Section */}
      <Box
        sx={{
          mb: 1,
          p: 1,
          // border: "1px solid orange",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, width: "auto" }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "var(--fx-surface-text, #0F172A)",
              width: "fit-content",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              mb: { xs: 2, sm: 0 },
            }}
          >
            Flow-DO
          </Typography>

          <IconButton
            onClick={() => setIsDescriptionOpen((v) => !v)}
            aria-label={isDescriptionOpen ? "Collapse description" : "Expand description"}
            sx={{
              transition: "transform 200ms ease",
              // transform: isDescriptionOpen ? "rotate(180deg)" : "rotate(0deg)",
            }}
          >
            <InfoIcon sx={{ color: "#9ad3ffff" }} />
          </IconButton>
        </Box>

        {/* NEW: collapsible body */}
        <Collapse in={isDescriptionOpen} timeout="auto" unmountOnExit>
          <Box
            sx={{
              p: 2,
              bgcolor: "var(--fx-colors-info-100, #DBEAFE)",
              borderRadius: 2,
              border: "1px solid var(--fx-surface-border, #E2E8F0)",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "var(--fx-surface-textsecondary, #64748B)",
                lineHeight: 1.6,
                fontSize: "0.875rem",
              }}
            >
              Manage your tasks with Kanban boards and list views. Create, edit, and organize tasks by assigning them to sections inside a page. Set
              priorities, categories and deadlines to keep everything organized.
            </Typography>
          </Box>
        </Collapse>
      </Box>
      <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid var(--fx-surface-border, #E2E8F0)", pb: 1 }}>
        <KanbanPageSelector
          pages={pages}
          selectedPageId={selectedPageId}
          onSelectPage={setSelectedPageId}
          onPageDeleted={handlePageDeleted}
          isPreviewMode={isPreviewMode}
        />
        <FlowDoHeader
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          onCreateTaskClick={handleCreateTaskClick}
          onCreateCategoryClick={handleCreateCategoryClick}
          isPreviewMode={isPreviewMode}
        />
      </Box>
      {viewMode === "kanban" ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "auto", alignItems: "stretch", bgcolor: "var(--fx-surface-base, #FFFFFF)", pb: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "row", gap: 1, width: "auto", alignItems: "stretch", bgcolor: "var(--fx-surface-base, #FFFFFF)" }}>
            {selectedPageId && (
              <KanbanCategorySelector
                categories={categories}
                selectedCategoryId={selectedCategoryId}
                onSelectCategory={setSelectedCategoryId}
                kanbanPageUid={selectedPageId}
                isPreviewMode={isPreviewMode}
              />
            )}
            <KanbanBoard tasks={tasks} isLoading={isLoadingData} onCreateTask={handleCreateTaskClick} isPreviewMode={isPreviewMode} />
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2, position: "absolute", bottom: 30, right: 30 }}>
            {/* <Box sx={{
              bgcolor: "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.9)",
              px: 2,
              py: 0.5,
              borderRadius: "20px",
              border: `1px solid ${isLimitReached ? colors.orange[500] : colors.gray[200]}`,
              display: "flex",
              alignItems: "center",
              gap: 1,
              boxShadow: "0 2px 10px rgba(0,0,0,0.05)"
            }}>
              <Typography sx={{ fontWeight: 800, fontSize: "0.75rem", color: isLimitReached ? colors.orange[500] : colors.gray[600] }}>
                Tasks: {currentTaskCount} / {TASK_LIMIT}
              </Typography>
            </Box> */}

            {/* <Tooltip title={isLimitReached ? "Your limit has been exceeded" : ""} arrow slotProps={{
              popper: {
                disablePortal: true,
                modifiers: [
                  {
                    name: "offset",
                    options: {
                      offset: [0, 0],
                    },
                  },
                ],
              }
            }}> */}
            <span>
              <Fab
                // disabled={isLimitReached}
                sx={{
                  // bgcolor: isLimitReached ? colors.gray[300] : colors.orange.base,
                  bgcolor: colors.orange.base,
                  "&:hover": {
                    // bgcolor: isLimitReached ? colors.gray[300] : `${colors.orange[700]}`,
                    bgcolor: colors.orange[700],
                  },
                }}
                color="primary"
                aria-label="add"
                onClick={() => handleCreateTaskClick("todo")}
              >
                <AddIcon />
              </Fab>
            </span>
            {/* </Tooltip> */}
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "row", gap: 1, width: "auto", alignItems: "stretch", bgcolor: "var(--fx-surface-base, #FFFFFF)" }}>
          {selectedPageId && (
            <KanbanCategorySelector
              categories={categories}
              selectedCategoryId={selectedCategoryId}
              onSelectCategory={setSelectedCategoryId}
              kanbanPageUid={selectedPageId}
              isPreviewMode={isPreviewMode}
            />
          )}
          <ListView tasks={tasks} isLoading={isLoadingData} onAddTask={handleCreateTaskClick} isPreviewMode={isPreviewMode} />
        </Box>
      )}

      {/* Create Task Modal */}
      {selectedCategoryId && (
        <BaseModalComponent
          modalLabel="Create New Task"
          modalKey="FlowDo-CreateTask"
          defaultSizes={{
            width: { xs: "95%", sm: "90%", md: "800px" },
            height: { xs: "95%", sm: "90%", md: "auto" },
          }}
          fitContentHeight={true}
          elementTrigger={<button ref={createTaskTriggerRef} type="button" style={{ position: "absolute", left: -99999, top: -99999 }} />}
        >
          <CreateTaskForm
            kanbanCategoryUid={selectedCategoryId}
            initialStatus={taskCreationStatus as any}
            onSuccess={handleTaskCreated}
            isPreviewMode={isPreviewMode}
          />
        </BaseModalComponent>
      )}

      {/* Create Category Modal */}
      {selectedPageId && (
        <BaseModalComponent
          modalLabel="Create New Category"
          modalKey="FlowDo-CreateCategory"
          defaultSizes={{
            width: { xs: "95%", sm: "90%", md: "800px" },
            height: { xs: "95%", sm: "90%", md: "auto" },
          }}
          fitContentHeight={true}
          elementTrigger={<button ref={createCategoryTriggerRef} type="button" style={{ position: "absolute", left: -99999, top: -99999 }} />}
        >
          <CreateCategoryForm kanbanPageUid={selectedPageId} onSuccess={handleCategoryCreated} />
        </BaseModalComponent>
      )}
    </Box>
  );
};
